import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn, UserPlus } from "lucide-react";

const navLinks = [
  { name: "About", path: "/about" },
  { name: "Global", path: "/global-presence" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Partners", path: "/become-customer" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when at top or scrolling up, hide when scrolling down
      if (currentScrollY < 100) {
        setHidden(false);
      } else if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setHidden(true);
      } else if (currentScrollY < lastScrollY) {
        setHidden(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <motion.nav
      data-testid="main-navbar"
      className={`fixed top-4 left-4 right-4 z-50 transition-transform duration-500 ${
        hidden ? "-translate-y-[140%]" : "translate-y-0"
      }`}
    >
      {/* Pill container */}
      <div className="nav-pill max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="font-heading text-xl sm:text-2xl font-bold tracking-wider text-ink uppercase">
            OPTR<span className="text-cyan">E</span>NEX
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              data-testid={`nav-${link.name.toLowerCase()}`}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] rounded-lg transition-all duration-300 ${
                location.pathname === link.path
                  ? "text-cyan bg-cyan-soft"
                  : "text-dim hover:text-ink hover:bg-white/5"
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-2">
          <a
            href="https://b2b.optrenex.com/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login"
            className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-dim hover:text-cyan transition-colors duration-300"
          >
            <LogIn size={14} />
            Login
          </a>
          <a
            href="https://b2b.optrenex.com/register"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-register"
            className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-dim hover:text-cyan transition-colors duration-300"
          >
            Register
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2 rounded-lg bg-white/5 text-ink hover:bg-white/10 transition-colors"
          aria-label={isOpen ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden mt-2 nav-pill max-w-6xl mx-auto overflow-hidden"
          >
            <div className="p-4 space-y-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`nav-mobile-${link.name.toLowerCase()}`}
                    className={`block px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] rounded-lg transition-all ${
                      location.pathname === link.path
                        ? "text-cyan bg-cyan-soft"
                        : "text-dim hover:text-ink hover:bg-white/5"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Auth Links */}
              <div className="pt-4 mt-4 border-t border-line space-y-2">
                <a
                  href="https://b2b.optrenex.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-login"
                  className="flex items-center gap-3 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-dim hover:text-cyan rounded-lg hover:bg-white/5 transition-all"
                >
                  <LogIn size={16} />
                  Login to Portal
                </a>
                <a
                  href="https://b2b.optrenex.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-register"
                  className="flex items-center gap-3 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] bg-cyan text-bg rounded-lg"
                >
                  <UserPlus size={16} />
                  Register Now
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
